import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

// Pan (arrastar) e zoom (roda) sobre a pré-visualização.
//
// O listener da roda é registado à mão com `{ passive: false }`: o React
// regista `onWheel` no root de forma passiva, e nesse caso o
// `preventDefault()` não faz nada e o browser deixa um aviso na consola.
// Com o listener próprio o scroll da página não foge enquanto se dá
// zoom no desenho.
//
// ENQUADRAMENTO AUTOMÁTICO (2026-08): o desenho é renderizado ao TAMANHO
// NATURAL DE PAPEL (`width: <N>mm`). Uma planta a 1:100 de um edifício de
// 30 m dá 310 mm, ou seja 1171 px de ecrã, e a pré-visualização tem umas
// centenas de px: sem enquadrar, via-se o canto superior esquerdo (quase
// sempre margem em branco) e o desenho parecia não existir. Agora o
// estado é DERIVADO: enquanto o utilizador não mexer, a transformação é
// a que faz o desenho caber e ficar centrado; assim que arrasta ou dá
// zoom, passa a mandar a transformação dele.

export interface PanZoomState {
  scale: number
  x: number
  y: number
}

export interface SizePx {
  w: number
  h: number
}

export const PANZOOM_MIN_SCALE = 0.0005
export const PANZOOM_MAX_SCALE = 40

/** Pixéis CSS por milímetro. O CSS fixa 1 in = 96 px, por isso a
 *  conversão é exacta e não depende do ecrã. */
export const PX_PER_MM = 96 / 25.4

/** Folga, em px, entre o desenho enquadrado e o bordo do contentor. */
export const FIT_PADDING_PX = 10

/** Espessura alvo, em PIXÉIS DE ECRÃ, dos traços da pré-visualização.
 *  Nada a ver com os milímetros de papel do PDF/SVG exportado: aqui o
 *  que interessa é que o traço se veja a qualquer zoom. */
export const PREVIEW_CUT_PX = 1.8
export const PREVIEW_PROJ_PX = 0.8

/** Transformação que enquadra um desenho de `naturalMm` (milímetros de
 *  papel) dentro de um contentor de `container` pixéis, centrado.
 *  Puro, para ser testável. */
export function fitTransform(
  naturalMm: SizePx,
  container: SizePx,
  padPx = FIT_PADDING_PX,
): PanZoomState {
  const natW = Math.max(naturalMm.w, 0) * PX_PER_MM
  const natH = Math.max(naturalMm.h, 0) * PX_PER_MM
  const availW = Math.max(container.w - padPx * 2, 1)
  const availH = Math.max(container.h - padPx * 2, 1)
  if (natW <= 0 || natH <= 0 || container.w <= 0 || container.h <= 0) {
    return { scale: 1, x: 0, y: 0 }
  }
  const scale = Math.min(
    PANZOOM_MAX_SCALE,
    Math.max(PANZOOM_MIN_SCALE, Math.min(availW / natW, availH / natH)),
  )
  return {
    scale,
    x: (container.w - natW * scale) / 2,
    y: (container.h - natH * scale) / 2,
  }
}

/** Espessura em UNIDADES DO SVG (= mm de papel) que dá `targetPx`
 *  pixéis no ecrã com o zoom actual.
 *
 *  Porquê calcular em vez de usar `vector-effect="non-scaling-stroke"`:
 *  o pan/zoom é uma transformação CSS num ascendente HTML, e o Chrome
 *  ESCALA na mesma o traço "non-scaling" nesse caso (medido: com um
 *  ascendente a `scale(50)`, um traço de 1,8 px passa a cobrir ±45 px).
 *  O `vector-effect` só neutraliza a escala do viewBox, que aqui já é 1
 *  (o `width` em mm é igual à largura do viewBox). Calcular a espessura
 *  é exacto e independente do browser. */
export function strokeUnitsForPx(targetPx: number, scale: number): number {
  const s = Number.isFinite(scale) && scale > 0 ? scale : 1
  return targetPx / (PX_PER_MM * s)
}

/** Novo estado depois de multiplicar a escala por `factor`, mantendo
 *  fixo o ponto (px, py) do contentor. É a primitiva do zoom: a roda do
 *  rato e o pinch de dois dedos só diferem na maneira de calcular o
 *  factor. Puro, para ser testável. */
export function scaleAt(
  state: PanZoomState,
  factor: number,
  px: number,
  py: number,
): PanZoomState {
  const f = Number.isFinite(factor) && factor > 0 ? factor : 1
  const next = Math.min(
    PANZOOM_MAX_SCALE,
    Math.max(PANZOOM_MIN_SCALE, state.scale * f),
  )
  const k = next / state.scale
  // O ponto sob o cursor tem de ficar onde estava: p = (p - t) * k + t'
  return {
    scale: next,
    x: px - (px - state.x) * k,
    y: py - (py - state.y) * k,
  }
}

/** Novo estado depois de um passo de zoom centrado no ponto do rato
 *  (coordenadas relativas ao contentor). Puro, para ser testável. */
export function zoomAt(
  state: PanZoomState,
  deltaY: number,
  px: number,
  py: number,
): PanZoomState {
  return scaleAt(state, Math.exp(-deltaY * 0.0015), px, py)
}

/** O contentor vem de fora (o chamador é que o renderiza): assim o
 *  objecto devolvido não transporta nenhuma ref, e ler `state` no render
 *  não dispara o `react-hooks/refs`.
 *
 *  `mounted` diz se o contentor está no DOM. É preciso porque o painel
 *  monta e desmonta a pré-visualização sempre que abre e fecha: sem esta
 *  dependência, o efeito do `ResizeObserver` corria uma vez só, com o
 *  `containerRef.current` ainda a null, e o desenho nunca chegava a ser
 *  enquadrado (via-se uma folha branca).
 *
 *  `naturalMm` é o tamanho do desenho em milímetros de papel; com ele o
 *  hook enquadra sozinho. Enquanto `userTransform` for null a
 *  transformação é derivada (enquadramento), por isso redimensionar a
 *  janela reenquadra sem efeito nenhum pelo meio.
 *
 *  `hostKey` identifica o DOCUMENTO onde a pré-visualização está. Destacar
 *  o 2D para uma janela do browser à parte muda o contentor do portal, e
 *  o React remonta o nó: sem esta dependência, o `ResizeObserver` e o
 *  listener da roda ficavam presos ao nó velho (a roda deixava de dar
 *  zoom na janela nova e o desenho nunca reenquadrava). */
export function useSvgPanZoom(
  containerRef: React.RefObject<HTMLDivElement | null>,
  mounted: boolean,
  resetKey: unknown,
  naturalMm: SizePx | null,
  hostKey?: unknown,
) {
  const [userTransform, setUserTransform] = useState<PanZoomState | null>(null)
  const [size, setSize] = useState<SizePx>({ w: 0, h: 0 })
  const dragRef = useRef<{
    startX: number
    startY: number
    origin: PanZoomState
  } | null>(null)

  // Reposição ao mudar de desenho: adoptada durante o render (padrão da
  // casa), não num efeito.
  const [prevKey, setPrevKey] = useState(resetKey)
  if (prevKey !== resetKey) {
    setPrevKey(resetKey)
    setUserTransform(null)
  }

  const natW = naturalMm?.w ?? 0
  const natH = naturalMm?.h ?? 0

  const fitted = useMemo(
    () => fitTransform({ w: natW, h: natH }, size),
    [natW, natH, size],
  )
  const state = userTransform ?? fitted

  // Tamanho do contentor: o desenho tem de reenquadrar quando a janela
  // do painel cresce (pega de canto) ou quando o browser muda de
  // tamanho.
  const measure = useCallback(() => {
    const node = containerRef.current
    if (!node) return
    const r = node.getBoundingClientRect()
    setSize((s) =>
      Math.abs(s.w - r.width) < 0.5 && Math.abs(s.h - r.height) < 0.5
        ? s
        : { w: r.width, h: r.height },
    )
  }, [containerRef])

  // Medir depois de cada commit. Neste painel TODAS as mudanças de
  // layout nascem de estado React (tamanho da janela, barra de desenhar
  // cortes, lista de camadas, rodapé de contagens), por isso isto chega
  // sozinho. E, ao contrário do `ResizeObserver`, não depende de a
  // página estar a compor frames: num separador em segundo plano o RO
  // nunca entrega nada e o desenho ficava com um enquadramento velho.
  useLayoutEffect(() => {
    if (mounted) measure()
  })

  // O `ResizeObserver` cobre o que o React não sabe: fontes a carregar,
  // zoom do browser, mudanças de layout de fora do painel.
  useEffect(() => {
    const node = mounted ? containerRef.current : null
    if (!node) return
    // O `ResizeObserver` do documento de origem não observa nós de outro
    // documento, por isso usa-se o da janela onde o nó vive.
    const RO = node.ownerDocument.defaultView?.ResizeObserver ?? ResizeObserver
    const ro = new RO(measure)
    ro.observe(node)
    return () => ro.disconnect()
  }, [containerRef, mounted, measure, hostKey])

  useEffect(() => {
    const node = mounted ? containerRef.current : null
    if (!node) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      const rect = node.getBoundingClientRect()
      const px = e.clientX - rect.left
      const py = e.clientY - rect.top
      setUserTransform((s) => zoomAt(s ?? fitted, e.deltaY, px, py))
    }
    node.addEventListener('wheel', onWheel, { passive: false })
    return () => node.removeEventListener('wheel', onWheel)
  }, [containerRef, mounted, fitted, hostKey])

  // Pinch de dois dedos: no telemóvel não há roda do rato, e sem isto o
  // desenho só se podia arrastar. Os ponteiros activos ficam numa ref
  // (não é estado: mudam a cada `pointermove` e não pintam nada).
  const pointersRef = useRef(new Map<number, { x: number; y: number }>())
  const pinchRef = useRef<{ startDist: number; origin: PanZoomState } | null>(
    null,
  )

  /** Distância e ponto médio dos dois ponteiros activos, em pixéis
   *  relativos ao contentor. Null com menos de dois dedos. */
  const pinchGeometry = useCallback((rect: DOMRect) => {
    const pts = Array.from(pointersRef.current.values())
    if (pts.length < 2) return null
    const [a, b] = pts
    return {
      dist: Math.hypot(b.x - a.x, b.y - a.y),
      mx: (a.x + b.x) / 2 - rect.left,
      my: (a.y + b.y) / 2 - rect.top,
    }
  }, [])

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
      if (pointersRef.current.size >= 2) {
        // Segundo dedo: passa a pinch e o arrasto pára aqui (senão o
        // desenho fugia com o dedo enquanto se dava zoom).
        const g = pinchGeometry(e.currentTarget.getBoundingClientRect())
        dragRef.current = null
        if (g && g.dist > 0) {
          pinchRef.current = { startDist: g.dist, origin: state }
        }
      } else {
        dragRef.current = {
          startX: e.clientX,
          startY: e.clientY,
          origin: state,
        }
      }
      // A captura fica para o FIM e protegida: com um ponteiro que o
      // browser não conhece (eventos sintéticos de teste) o
      // `setPointerCapture` lança, e a lógica de cima não podia ficar
      // refém disso.
      try {
        e.currentTarget.setPointerCapture?.(e.pointerId)
      } catch {
        /* ponteiro sem captura possível: o arrasto funciona à mesma */
      }
    },
    [state, pinchGeometry],
  )

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (pointersRef.current.has(e.pointerId)) {
        pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
      }
      const pinch = pinchRef.current
      if (pinch) {
        const g = pinchGeometry(e.currentTarget.getBoundingClientRect())
        if (!g || g.dist <= 0) return
        setUserTransform(
          scaleAt(pinch.origin, g.dist / pinch.startDist, g.mx, g.my),
        )
        return
      }
      const d = dragRef.current
      if (!d) return
      setUserTransform({
        scale: d.origin.scale,
        x: d.origin.x + (e.clientX - d.startX),
        y: d.origin.y + (e.clientY - d.startY),
      })
    },
    [pinchGeometry],
  )

  const onPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    pointersRef.current.delete(e.pointerId)
    if (pointersRef.current.size < 2) pinchRef.current = null
    dragRef.current = null
    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId)
    } catch {
      /* nada capturado: nada a libertar */
    }
  }, [])

  /** Volta ao enquadramento automático. */
  const reset = useCallback(() => setUserTransform(null), [])

  return {
    state,
    reset,
    /** true quando o utilizador mexeu (o botão de enquadrar só faz
     *  sentido nesse caso). */
    adjusted: userTransform !== null,
    containerSize: size,
    handlers: { onPointerDown, onPointerMove, onPointerUp },
  }
}
